import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Chip, Grid, Collapse, ToggleButton,
  ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem,
  Pagination, Accordion, AccordionSummary, AccordionDetails, Checkbox,
  Autocomplete, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortIcon from '@mui/icons-material/Sort';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { searchTabledDocuments } from '../services/api';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import ExportCSVButton from './ExportCSVButton';
import RSSButton from './RSSButton';
import { SaveSearchButton } from './saved-searches';

// #260: explicit filter-rail ordering. The plan from ali-pm:
// is_disallowable → originating_chamber → paper_type → document_type →
// category → department → parliament_number → years. Years are rendered
// via DateHierarchyFilter ABOVE this list (handles year/month/day drill-down).
const FACET_ORDER = [
  'disallowables',
  'chambers',
  'paper_types',
  'document_types',
  'categories',
  'departments',
  'authors',
  'parliament_numbers',
];

// "Primary discriminating filter" per CLAUDE.md: only Chamber opens by default.
const DEFAULT_EXPANDED_FACETS = new Set(['chambers']);

// #260 sort options. Default = Date (newest).
const DEFAULT_SORT = 'date:desc';
const SORT_OPTIONS = [
  { value: 'date:desc', label: 'Date (newest)' },
  { value: 'date:asc', label: 'Date (oldest)' },
  { value: '_score', label: 'Relevance' },
  { value: 'title.keyword:asc', label: 'Title (A-Z)' },
  { value: 'title.keyword:desc', label: 'Title (Z-A)' },
];

// Facets where coverage is partial across the corpus (parlinfo source carries
// minimal metadata vs. the OTD source which is rich). Display this microcopy
// hint near the affected filters to set expectations.
const PARTIAL_COVERAGE_FACETS = new Set([
  'paper_types', 'document_types', 'categories', 'departments', 'authors', 'disallowables',
]);

export default function TabledDocumentsSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [activeFilters, setActiveFilters] = useState({});
  const [facets, setFacets] = useState({});
  const [sortBy, setSortBy] = useState(() => {
    const urlSort = searchParams.get('sort');
    return SORT_OPTIONS.some(o => o.value === urlSort) ? urlSort : DEFAULT_SORT;
  });

  // Date drill-down state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    documentType: '',
    category: '',
    department: '',
    parliamentNumber: '',
    dateFrom: '',
    dateTo: ''
  });

  // Options for autocomplete fields
  const [availableOptions, setAvailableOptions] = useState({
    departments: [],
    documentTypes: []
  });

  // Fetch autocomplete options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await searchTabledDocuments('*', 1, 100);
        if (data.hits) {
          const departments = [...new Set(data.hits.map(h => h.department).filter(Boolean))].sort();
          const documentTypes = [...new Set(data.hits.map(h => h.document_type).filter(Boolean))].sort();
          setAvailableOptions({ departments, documentTypes });
        }
      } catch (e) {
        console.error('Failed to fetch options:', e);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.documentType) parts.push(`document_type:"${form.documentType}"`);
    if (form.category) parts.push(`category:"${form.category}"`);
    if (form.department) parts.push(`department:"${form.department}"`);
    if (form.parliamentNumber) parts.push(`parliament_number:${form.parliamentNumber}`);
    if (form.dateFrom) parts.push(`date:>=${form.dateFrom}`);
    if (form.dateTo) parts.push(`date:<=${form.dateTo}`);

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  // Mapping: facet rail bucket name → backend dedicated filter param.
  // These travel as their own query params (not embedded Lucene clauses) so
  // the backend can term-match against the .keyword subfield and apply
  // chamber-variant normalisation. is_disallowable is boolean-shaped.
  const FACET_TO_PARAM = {
    chambers: 'originating_chamber',
    paper_types: 'paper_type',
    document_types: 'document_type',
    categories: 'category',
    departments: 'department',
    authors: 'author',
    parliament_numbers: 'parliament_number',
    disallowables: 'is_disallowable',
  };

  const handleSearch = async (
    searchQuery = null,
    newPage = 1,
    filters = activeFilters,
    year = selectedYear,
    month = selectedMonth,
    pageSize = perPage,
    sort = sortBy,
  ) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    // Build dedicated-param filter map. Most facets are single-select via
    // first-value; disallowables is mapped Yes/No → true/false.
    const apiFilters = {};
    Object.entries(filters).forEach(([facetName, values]) => {
      if (!values || values.length === 0) return;
      const param = FACET_TO_PARAM[facetName];
      if (!param) return;
      if (param === 'is_disallowable') {
        // Yes/No labels → boolean
        const v = String(values[0]);
        apiFilters[param] = v === 'Yes' ? 'true' : v === 'No' ? 'false' : v.toLowerCase();
      } else {
        apiFilters[param] = values[0];
      }
    });

    setLoading(true);
    setError(null);
    try {
      const data = await searchTabledDocuments(
        baseQuery, newPage, pageSize, year, month, sort, apiFilters,
      );
      setResults(data);
      setPage(newPage);
      if (data.facets) {
        setFacets(data.facets);
      }
      const newParams = { q: baseQuery, mode: searchMode };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      if (sort && sort !== DEFAULT_SORT) newParams.sort = sort;
      setSearchParams(newParams);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setLoading(false);
    setInitialLoading(false);
  };

  // Date drill-down handlers
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, year, null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, activeFilters, year, month);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, null, null);
  };

  // #260: each filter is single-select (clicking the same bucket toggles it
  // off). Multi-select would require the backend to accept lists per
  // dedicated param, which it currently doesn't — keeping it simple matches
  // the chamber-as-radio shape from BillsSearch.
  const handleFacetClick = (facetName, value) => {
    const newFilters = { ...activeFilters };
    if (newFilters[facetName]?.[0] === value) {
      delete newFilters[facetName];
    } else {
      newFilters[facetName] = [value];
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const facetDisplayName = (facetName) => {
    const names = {
      chambers: 'Chamber',
      paper_types: 'Paper Type',
      document_types: 'Document Type',
      categories: 'Category',
      departments: 'Department',
      authors: 'Author',
      parliament_numbers: 'Parliament',
      disallowables: 'Disallowable',
    };
    return names[facetName] || facetName;
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, newSize);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    handleSearch(null, 1, activeFilters, selectedYear, selectedMonth, perPage, newSort);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      documentType: '',
      category: '',
      department: '',
      parliamentNumber: '',
      dateFrom: '',
      dateTo: ''
    });
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setSortBy(DEFAULT_SORT);
    setSearchParams({});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getDocumentTypeColor = (type) => {
    if (!type) return 'default';
    const lowerType = type.toLowerCase();
    if (lowerType.includes('annual report')) return 'primary';
    if (lowerType.includes('government response')) return 'success';
    if (lowerType.includes('treaty')) return 'warning';
    if (lowerType.includes('regulation') || lowerType.includes('legislative')) return 'error';
    if (lowerType.includes('report')) return 'info';
    return 'default';
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ color: '#ff9800', fontSize: 32 }} />
          <Typography variant="h4">Tabled Documents</Typography>
        </Box>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search tabled documents..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4, bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search in document titles and content"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.documentTypes}
                value={form.documentType}
                onChange={(e, v) => setForm({...form, documentType: v || ''})}
                renderInput={(params) => <TextField {...params} label="Document Type" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={form.category} onChange={handleChange('category')} label="Category">
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="Presented by the President">Presented by the President</MenuItem>
                  <MenuItem value="Government Document">Government Document</MenuItem>
                  <MenuItem value="Committee Report">Committee Report</MenuItem>
                  <MenuItem value="Private Senator">Private Senator</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.departments}
                value={form.department}
                onChange={(e, v) => setForm({...form, department: v || ''})}
                renderInput={(params) => <TextField {...params} label="Department" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Parliament</InputLabel>
                <Select value={form.parliamentNumber} onChange={handleChange('parliamentNumber')} label="Parliament">
                  <MenuItem value="">All Parliaments</MenuItem>
                  <MenuItem value="48">48th Parliament</MenuItem>
                  <MenuItem value="47">47th Parliament</MenuItem>
                  <MenuItem value="46">46th Parliament</MenuItem>
                  <MenuItem value="45">45th Parliament</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={form.dateFrom}
                onChange={handleChange('dateFrom')}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Date To"
                type="date"
                value={form.dateTo}
                onChange={handleChange('dateTo')}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2, bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Loading skeleton for initial load */}
      {initialLoading && <SearchSkeleton count={5} showFacets={true} />}

      {/* Error state */}
      {!initialLoading && error && (
        <ErrorState
          message={error}
          onRetry={() => handleSearch()}
          themeColor="#ff9800"
        />
      )}

      {/* Empty state when no results */}
      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="tableddocuments"
          onSuggestionClick={(q) => {
            setQuery(q);
            handleSearch(q);
          }}
          onReset={handleReset}
          themeColor="#ff9800"
        />
      )}

      {/* Results display */}
      {!initialLoading && !error && results && results.total > 0 && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {Object.keys(facets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {(Object.keys(activeFilters).length > 0 || selectedYear) && (
                    <Button size="small" onClick={() => { clearAllFilters(); clearDateDrillDown(); }}>Clear All</Button>
                  )}
                </Box>

                {/* Date Hierarchy Filter (years/months/days) */}
                {(facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0) && (
                  <DateHierarchyFilter
                    facets={facets}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedDay={selectedDay}
                    onYearClick={handleYearClick}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleDayClick}
                    onClearAll={clearDateDrillDown}
                    themeColor="#ff9800"
                  />
                )}

                {/* #260 coverage note: parlinfo source carries minimal metadata
                    for paper_type/author/document_type/category/department/
                    disallowable. Set expectations up front. */}
                <Alert severity="info" sx={{ mt: 1, mb: 2, fontSize: '0.75rem', py: 0.5 }}>
                  Some filters (paper type, author, department, disallowable) apply
                  primarily to documents tabled since 2022 — older ParlInfo records
                  carry less structured metadata.
                </Alert>

                {FACET_ORDER.map((facetName) => (
                  facets[facetName] && facets[facetName].length > 0 && (
                    <Accordion key={facetName} defaultExpanded={DEFAULT_EXPANDED_FACETS.has(facetName)}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {facetDisplayName(facetName)}
                          {PARTIAL_COVERAGE_FACETS.has(facetName) && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              *
                            </Typography>
                          )}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {facets[facetName].slice(0, 25).map((facet) => {
                            const valStr = String(facet.value);
                            const isSelected = activeFilters[facetName]?.[0] === valStr;
                            const label = facetName === 'parliament_numbers'
                              ? `Parliament ${facet.value}`
                              : facet.value;
                            return (
                              <ListItem key={facet.value} disablePadding>
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onChange={() => handleFacetClick(facetName, valStr)}
                                />
                                <ListItemText
                                  primary={`${label} (${facet.count.toLocaleString()})`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          {/* Results */}
          <Grid item xs={12} md={Object.keys(facets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              {/* Std results layout (CLAUDE.md). Row 1: count (left) + pagination (right). */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">
                  {results.total.toLocaleString()} {results.total === 1 ? 'document' : 'documents'} found
                </Typography>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              {/* Row 2: Per page · Sort · Export · Save · RSS */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="td-sort-label">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SortIcon fontSize="small" />
                      Sort by
                    </Box>
                  </InputLabel>
                  <Select
                    labelId="td-sort-label"
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    label="Sort by"
                  >
                    {SORT_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ExportCSVButton
                  dataType="tableddocuments"
                  query={query || '*'}
                  totalResults={results.total}
                />
                <SaveSearchButton
                  searchType="tableddocuments"
                  queryText={query || '*'}
                  parameters={{ sort: sortBy, page, per_page: perPage, ...activeFilters }}
                  resultCount={results.total}
                />
                <RSSButton collection="tableddocuments" query={query || '*'} />
              </Box>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography
                              variant="resultTitle"
                              component={RouterLink}
                              to={`/tableddocuments/${hit.id}`}
                              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {hit.title || 'Untitled Document'}
                            </Typography>
                            {hit.originating_chamber && (
                              <Chip
                                label={hit.originating_chamber}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {hit.document_type && (
                              <Chip
                                label={hit.document_type}
                                size="small"
                                color={getDocumentTypeColor(hit.document_type)}
                              />
                            )}
                            {hit.parliament_number && (
                              <Chip
                                label={`Parliament ${hit.parliament_number}`}
                                size="small"
                                variant="outlined"
                                clickable
                                component={RouterLink}
                                to={`/parliaments/${hit.parliament_number}`}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {[formatDate(hit.date), hit.category, hit.paper_type].filter(Boolean).join(' · ')}
                            </Typography>
                            {hit.department && (
                              <Typography variant="body2" color="text.secondary">
                                {hit.department}
                              </Typography>
                            )}
                            {hit.author && hit.author !== hit.department && (
                              <Typography variant="body2" color="text.secondary">
                                Author: {hit.author}
                              </Typography>
                            )}
                            {hit.file_count > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {hit.file_count} file{hit.file_count > 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
